import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BarChart3, ArrowLeft, ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown,
  IndianRupee, Percent, Wallet, Receipt, Calendar, Loader2
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';

interface RevenueData {
  dateFrom: string;
  dateTo: string;
  totalCollected: number;
  totalInvoiced: number;
  totalDue: number;
  totalExpenses: number;
  profit: number;
  profitMargin: number;
  expensesByLabel: { label: string; total: number; count: number }[];
  monthlyCollected: { month: string; total: number }[];
  monthlyExpenses: { month: string; total: number }[];
}

const labelColors: Record<string, string> = {
  'Teacher Salary': '#10b981',
  'Management': '#3b82f6',
  'Infrastructure': '#f59e0b',
  'Nitish': '#8b5cf6',
  'Deepak': '#06b6d4',
  'Kunal': '#f43f5e',
  'Petrol Spend': '#f97316',
  'AD Spend': '#ec4899',
};

function getLabelColor(label: string) {
  return labelColors[label] || '#6b7280';
}

export default function RevenuePage() {
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2027-01-01');

  const { data: revenueData, isLoading } = useQuery({
    queryKey: ['revenue-page', dateFrom, dateTo],
    queryFn: async () => (await api.get<RevenueData>(`/api/expenses/revenue?dateFrom=${dateFrom}&dateTo=${dateTo}`)).data,
  });

  const monthlyData = useMemo(() => {
    if (!revenueData) return [];
    const collectedMap: Record<string, number> = {};
    const expenseMap: Record<string, number> = {};
    (revenueData.monthlyCollected || []).forEach((m: any) => { collectedMap[m.month] = m.total; });
    (revenueData.monthlyExpenses || []).forEach((m: any) => { expenseMap[m.month] = m.total; });
    const allMonths = new Set([...Object.keys(collectedMap), ...Object.keys(expenseMap)]);
    return Array.from(allMonths).sort().map(month => ({
      month,
      collected: collectedMap[month] || 0,
      expenses: expenseMap[month] || 0,
      profit: (collectedMap[month] || 0) - (expenseMap[month] || 0),
    }));
  }, [revenueData]);

  const maxMonthly = useMemo(() => {
    if (!monthlyData.length) return 1;
    return Math.max(...monthlyData.map(m => Math.max(m.collected, m.expenses)), 1);
  }, [monthlyData]);

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 p-5 text-white shadow-xl overflow-hidden relative">
        <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Link to="/expenses" className="p-1 rounded-lg hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-white/75">Institute Finance</p>
              <h1 className="text-2xl font-black mt-1">Revenue Overview</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range */}
      <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-3.5 h-3.5 text-purple-500" />
          <span className="text-xs font-semibold text-light-text dark:text-dark-text">Date Range</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="flex-1 h-9 rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-2.5 text-xs text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-purple-500/50 dark:[color-scheme:dark]"
          />
          <span className="text-[10px] text-light-text-muted flex-shrink-0">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="flex-1 h-9 rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-2.5 text-xs text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-purple-500/50 dark:[color-scheme:dark]"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[1,2,3,4].map(i => <div key={i} className="h-24 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />)}
          </div>
          <div className="h-48 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />
        </div>
      ) : revenueData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}
              className="rounded-xl bg-lime-50 dark:bg-lime-500/10 border border-lime-200 dark:border-lime-500/20 p-3 min-w-0">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-6 h-6 rounded-lg bg-lime-500/20 flex items-center justify-center">
                  <ArrowUpRight className="w-3.5 h-3.5 text-lime-600" />
                </div>
                <p className="text-[10px] font-medium text-lime-700 dark:text-lime-300">Collected</p>
              </div>
              <p className="text-base sm:text-lg font-bold text-lime-700 dark:text-lime-300 whitespace-nowrap overflow-hidden text-ellipsis">{formatCurrency(revenueData.totalCollected)}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 p-3 min-w-0">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-6 h-6 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Receipt className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <p className="text-[10px] font-medium text-amber-700 dark:text-amber-300">Invoiced</p>
              </div>
              <p className="text-base sm:text-lg font-bold text-amber-700 dark:text-amber-300 whitespace-nowrap overflow-hidden text-ellipsis">{formatCurrency(revenueData.totalInvoiced)}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 p-3 min-w-0">
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-6 h-6 rounded-lg bg-rose-500/20 flex items-center justify-center">
                  <ArrowDownRight className="w-3.5 h-3.5 text-rose-600" />
                </div>
                <p className="text-[10px] font-medium text-rose-700 dark:text-rose-300">Expenses</p>
              </div>
              <p className="text-base sm:text-lg font-bold text-rose-700 dark:text-rose-300 whitespace-nowrap overflow-hidden text-ellipsis">{formatCurrency(revenueData.totalExpenses)}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className={`rounded-xl border p-3 min-w-0 ${revenueData.profit >= 0 ? 'bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20' : 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20'}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${revenueData.profit >= 0 ? 'bg-purple-500/20' : 'bg-amber-500/20'}`}>
                  {revenueData.profit >= 0 ? <TrendingUp className="w-3.5 h-3.5 text-purple-600" /> : <TrendingDown className="w-3.5 h-3.5 text-amber-600" />}
                </div>
                <p className={`text-[10px] font-medium ${revenueData.profit >= 0 ? 'text-purple-700 dark:text-purple-300' : 'text-amber-700 dark:text-amber-300'}`}>Profit</p>
              </div>
              <p className={`text-base sm:text-lg font-bold whitespace-nowrap overflow-hidden text-ellipsis ${revenueData.profit >= 0 ? 'text-purple-700 dark:text-purple-300' : 'text-amber-700 dark:text-amber-300'}`}>
                {formatCurrency(revenueData.profit)}
              </p>
              <p className={`text-[9px] mt-0.5 ${revenueData.profit >= 0 ? 'text-purple-500' : 'text-amber-500'}`}>
                {revenueData.profitMargin}% margin
              </p>
            </motion.div>
          </div>

          {/* Due Amount */}
          <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-rose-500" />
                <span className="text-xs font-semibold text-light-text dark:text-dark-text">Total Due</span>
              </div>
              <span className="text-sm font-bold text-rose-600 dark:text-rose-400">{formatCurrency(revenueData.totalDue)}</span>
            </div>
            <div className="mt-2 w-full h-2 bg-light-elevated dark:bg-dark-elevated rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-lime-500 transition-all duration-500"
                style={{ width: `${revenueData.totalInvoiced > 0 ? Math.round((revenueData.totalCollected / revenueData.totalInvoiced) * 100) : 0}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-light-text-muted">Collected: {formatCurrency(revenueData.totalCollected)}</span>
              <span className="text-[9px] text-light-text-muted">{revenueData.totalInvoiced > 0 ? Math.round((revenueData.totalCollected / revenueData.totalInvoiced) * 100) : 0}%</span>
            </div>
          </div>

          {/* Monthly Chart */}
          {monthlyData.length > 0 && (
            <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-purple-500" />
                <span className="text-xs font-semibold text-light-text dark:text-dark-text">Monthly Breakdown</span>
              </div>
              <div className="space-y-2">
                {monthlyData.map((m, idx) => (
                  <motion.div
                    key={m.month}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="space-y-1"
                  >
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-light-text-muted font-medium w-16">{m.month}</span>
                      <div className="flex items-center gap-3 flex-1 justify-end">
                        <span className="text-lime-600 dark:text-lime-400 font-medium">{formatCurrency(m.collected)}</span>
                        <span className="text-rose-600 dark:text-rose-400 font-medium">{formatCurrency(m.expenses)}</span>
                        <span className={`font-bold w-20 text-right ${m.profit >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {formatCurrency(m.profit)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-0.5 h-3 rounded-full overflow-hidden bg-light-elevated dark:bg-dark-elevated">
                      <div
                        className="h-full rounded-l-full bg-lime-500 transition-all duration-500"
                        style={{ width: `${maxMonthly > 0 ? (m.collected / maxMonthly) * 100 : 0}%` }}
                        title={`Collected: ${formatCurrency(m.collected)}`}
                      />
                      <div
                        className="h-full rounded-r-full bg-rose-500 transition-all duration-500"
                        style={{ width: `${maxMonthly > 0 ? (m.expenses / maxMonthly) * 100 : 0}%` }}
                        title={`Expenses: ${formatCurrency(m.expenses)}`}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-3 pt-2 border-t border-light-border dark:border-dark-border">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-lime-500" />
                  <span className="text-[9px] text-light-text-muted">Collected</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-rose-500" />
                  <span className="text-[9px] text-light-text-muted">Expenses</span>
                </div>
              </div>
            </div>
          )}

          {/* Expenses by Category */}
          {revenueData.expensesByLabel?.length > 0 && (
            <div className="bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border rounded-2xl p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-4 h-4 text-rose-500" />
                <span className="text-xs font-semibold text-light-text dark:text-dark-text">Expenses by Category</span>
              </div>
              <div className="space-y-2">
                {revenueData.expensesByLabel.map((item, idx) => {
                  const color = getLabelColor(item.label);
                  const pct = revenueData.totalExpenses > 0 ? Math.round((item.total / revenueData.totalExpenses) * 100) : 0;
                  return (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                          <span className="text-xs font-medium text-light-text dark:text-dark-text">{item.label}</span>
                          <span className="text-[9px] text-light-text-muted">({item.count} items)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-light-text dark:text-dark-text">{formatCurrency(item.total)}</span>
                          <span className="text-[9px] text-light-text-muted">{pct}%</span>
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-light-elevated dark:bg-dark-elevated rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
